// src/components/AGTFit/fit/FitProfile.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Tab,
  useTheme,
  useMediaQuery,
  IconButton,
  Dialog
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Settings,
  ChevronRight,
  ChevronLeft,
  BadgeCheck
} from 'lucide-react';
import Health from '../health/Health';
import AppleSpinner from '../../common/AppleSpinner';
import agtfitService from '../service/agtfitService';
import WorkoutModeButton from '../workout/WorkoutModeButton';
import DetailedNutrition from '../food/DetailedNutrition';
import TodaysActivity from '../workout/TodaysActivity';
import FoodSearch from '../food/FoodSearch';
import WorkoutStats from '../workout/move/WorkoutStats';
import FitGoals from '../goals/FitGoals';
import { useAGTFitProContext } from '../context/AGTFitProContext';

// ===========================================
// 🎬 VARIANTES DE ANIMACIÓN TIPO macOS
// ===========================================
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Color principal
const FIT_ORANGE = '#FF6B00';

// Removed unused constant: WALLPAPER_COLORS

// Componentes estilizados
const ProfileContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4, 3, 3), // Añadido padding superior
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'transparent'
    : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
  maxWidth: '1200px',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 1, 2),
  }
}));

// Removed unused component: StatsCard

// Nuevo componente para el contenedor de nutrición con fondo transparente
const NutritionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  width: '100%',
  maxWidth: 800,
  margin: '0 auto',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

// Nuevo componente para el calendario
const CalendarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 800,
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const DayContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})(({ theme, isSelected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  position: 'relative',
  cursor: 'pointer',
  width: '40px',
  '&:hover': {
    opacity: 0.8,
  }
}));

// Corregido para que sea perfectamente circular
const DayLetter = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})(({ theme, isSelected }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 4,
  background: isSelected ? 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)' : 'transparent',
  color: isSelected ? '#FFFFFF' : theme.palette.text.primary,
  fontWeight: 500,
  minWidth: 28, // Asegura un ancho mínimo constante
  minHeight: 28, // Asegura una altura mínima constante
  boxSizing: 'border-box', // Mantén esto
  padding: 0, // Elimina cualquier padding que pueda afectar la forma
  lineHeight: 1, // Normaliza la altura de línea
  fontSize: '0.875rem', // Tamaño de fuente consistente
}));

const DayNumber = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})(({ theme, isSelected }) => ({
  fontWeight: isSelected ? 600 : 400,
  color: isSelected ? '#FF6B00' : theme.palette.text.primary,
  fontSize: '0.875rem',
}));

const DayDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})(({ theme, isSelected }) => ({
  width: 4,
  height: 4,
  borderRadius: '50%',
  marginTop: 4,
  background: isSelected
    ? 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)'
    : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
  aspectRatio: '1/1', // Asegura que sea perfectamente circular
  boxSizing: 'border-box', // Asegura que el padding no afecte las dimensiones
  overflow: 'hidden', // Evita desbordamiento que podría afectar la forma
  minWidth: 4, // Establece un ancho mínimo
  minHeight: 4, // Establece una altura mínima
}));

// Removed unused component: MacroCard

// Removed unused component: ActionButton

// Removed unused component: OutlineButton

// Título con gradiente
const GradientTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'inline-block',
}));

// NavigationBar estilizado con bordes líquidos iOS
const StyledTabs = styled(Box)(({ theme }) => ({
  minHeight: '40px',
  height: '40px',
  background: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(128, 128, 128, 0.3)'
    : (theme.palette.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.94)'
      : 'rgba(242, 242, 247, 1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  padding: '6px',
  borderRadius: '24px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  maxWidth: '500px',
  margin: '0 auto',
  marginBottom: theme.spacing(5),

  // Borde principal
  border: theme.palette.actualMode === 'wallpaper'
    ? '1px solid rgba(128, 128, 128, 0.3)'
    : (theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.12)'
      : '1px solid rgba(255, 255, 255, 1)'),
  
  // Pseudo-elemento para el borde degradado estilo iOS (no se muestra en wallpaper)
  '&::before': {
    content: theme.palette.actualMode === 'wallpaper' ? 'none' : '""',
    position: 'absolute',
    top: '-1px',
    left: '-1px',
    right: '-1px',
    bottom: '-1px',
    borderRadius: 'inherit',
    padding: '1px',

    background: theme.palette.mode === 'dark' ? `
      radial-gradient(ellipse at top, 
        rgba(255, 255, 255, 0.2) 0%,
        rgba(255, 255, 255, 0.15) 15%,
        rgba(255, 255, 255, 0.08) 40%,
        rgba(255, 255, 255, 0.04) 60%,
        rgba(255, 255, 255, 0.02) 80%,
        rgba(255, 255, 255, 0.08) 100%
      ),
      radial-gradient(ellipse at bottom, 
        rgba(255, 255, 255, 0.15) 0%,
        rgba(255, 255, 255, 0.12) 15%,
        rgba(255, 255, 255, 0.06) 40%,
        rgba(255, 255, 255, 0.03) 60%,
        rgba(255, 255, 255, 0.015) 80%,
        rgba(255, 255, 255, 0.06) 100%
      ),
      linear-gradient(90deg,
        rgba(255, 255, 255, 0.15) 0%,
        rgba(255, 255, 255, 0.06) 20%,
        rgba(255, 255, 255, 0.03) 50%,
        rgba(255, 255, 255, 0.06) 80%,
        rgba(255, 255, 255, 0.15) 100%
      )
    ` : `
      radial-gradient(ellipse at top, 
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 1) 15%,
        rgba(255, 255, 255, 1) 40%,
        rgba(255, 255, 255, 1) 60%,
        rgba(255, 255, 255, 1) 80%,
        rgba(255, 255, 255, 1) 100%
      ),
      radial-gradient(ellipse at bottom, 
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 1) 15%,
        rgba(255, 255, 255, 1) 40%,
        rgba(255, 255, 255, 1) 60%,
        rgba(255, 255, 255, 1) 80%,
        rgba(255, 255, 255, 1) 100%
      ),
      linear-gradient(90deg,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 1) 20%,
        rgba(255, 255, 255, 1) 50%,
        rgba(255, 255, 255, 1) 80%,
        rgba(255, 255, 255, 1) 100%
      )
    `,
    
    mask: `
      radial-gradient(ellipse, transparent 48%, black 50%),
      linear-gradient(black, black)
    `,
    WebkitMask: `
      radial-gradient(ellipse, transparent 48%, black 50%),
      linear-gradient(black, black)
    `,
    maskComposite: 'subtract',
    WebkitMaskComposite: 'subtract',
    pointerEvents: 'none',
    zIndex: 2,
  },
  
  // Pseudo-elemento para fondo sutil adicional (no se muestra en wallpaper)
  '&::after': {
    content: theme.palette.actualMode === 'wallpaper' ? 'none' : '""',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    borderRadius: 'inherit',

    background: theme.palette.mode === 'dark' ? `
      radial-gradient(ellipse 120% 80% at 50% 50%, 
        rgba(255, 255, 255, 0.01) 0%,
        transparent 70%
      )
    ` : `
      radial-gradient(ellipse 120% 80% at 50% 50%, 
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      )
    `,
    
    pointerEvents: 'none',
    zIndex: 1,
  },
  
  // Asegurar que el contenido esté por encima de los pseudo-elementos
  '& > *': {
    position: 'relative',
    zIndex: 3,
  }
}));

// TabIndicator modificado para usar transform
const TabIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  height: '28px',
  borderRadius: '24px',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(255, 255, 255, 0.2)'
    : (theme.palette.mode === 'dark'
      ? 'rgba(118, 118, 128, 0.24)'
      : '#FFFFFF'),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: 0,
  boxShadow: theme.palette.actualMode === 'wallpaper'
    ? 'none'
    : (theme.palette.mode === 'dark'
      ? 'none'
      : '0 2px 8px rgba(0, 0, 0, 0.04)'),
}));

// StyledTab con ancho adaptable al contenido y efecto de vidrio SOLO en seleccionado
const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: '28px',
  height: '28px',
  minWidth: 'auto', // Eliminar el ancho mínimo fijo
  padding: '0 16px', // Aumentar el padding horizontal para dar más espacio al texto
  borderRadius: '24px',
  color: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(255, 255, 255, 0.7)'
    : (theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.7)'
      : '#666666'),
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  zIndex: 1,
  transition: 'all 0.2s',
  backgroundColor: 'transparent',
  whiteSpace: 'nowrap', // Evitar que el texto se rompa en varias líneas

  // Sin efectos en estado normal
  boxShadow: 'none',
  
  '&.Mui-selected': {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    
    // Efecto de vidrio SOLO en la parte superior cuando está seleccionado
    boxShadow: theme.palette.mode === 'dark' 
      ? 'inset 0 1px 0 rgba(255, 255, 255, 0.3)'
      : 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
  },
  
  '&:hover:not(.Mui-selected)': {
    backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
    backgroundClip: 'text',
    textFillColor: 'transparent',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundColor: 'transparent',
    
    // Sin efecto de vidrio en hover, solo cambio de color
    boxShadow: 'none',
  },
}));

const NavigationButton = styled('button')(({ theme }) => ({
  minHeight: '28px',
  height: '28px',
  width: '28px',
  minWidth: '28px',
  padding: '0',
  marginRight: '6px',
  marginLeft: '6px',
  border: 'none',
  borderRadius: '30px',
  backgroundColor: 'transparent',
  color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : FIT_ORANGE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 1,
  transition: 'all 0.2s',
  outline: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 0.8,
  },
  '&:active': {
    transform: 'scale(0.95)',
    backgroundColor: 'transparent',
  },
}));

// Removed unused component: CircularProgressContainer

// Removed unused component: CircularProgressLabel

// Removed unused component: IconBox

// Removed unused component: FoodItemCard

// Función para redondear valores al entero más cercano
const roundNumber = (num) => {
  if (num === undefined || num === null) return 0;
  const decimal = num - Math.floor(num);
  return decimal >= 0.5 ? Math.ceil(num) : Math.floor(num);
};

// Removed unused function: useDebounce

// Componente principal
const FitProfile = ({ user, onEditProfile, onStartWorkout }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showHealthTab, setShowHealthTab] = useState(false);
  const [stats, setStats] = useState({
    calories: { consumed: 0, goal: 0, burned: 0 },
    protein: { consumed: 0, goal: 0 },
    carbs: { consumed: 0, goal: 0 },
    fats: { consumed: 0, goal: 0 },
    water: { consumed: 0, goal: 8 },
    steps: { count: 0, goal: 10000 }
  });
  const [todayMeals, setTodayMeals] = useState([]);
  const [todayExercises, setTodayExercises] = useState([]);
  const [greeting, setGreeting] = useState("");
  const [openFoodSearch, setOpenFoodSearch] = useState(false);
  const [selectedMealType] = useState('snack');
  
  // Estado para el calendario - Usando la fecha actual del sistema
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarWeek, setCalendarWeek] = useState([]);
  
  // Referencias para los elementos de pestaña
  const tabsContainerRef = useRef(null);
  const tabRefs = useRef([]);
  if (tabRefs.current.length === 0) {
    tabRefs.current = Array(5).fill(null).map(() => ({ current: null }));
  }
  const todaysActivityRef = useRef(null);
  const { isPro } = useAGTFitProContext();
  
  // Estado para el indicador de pestañas
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: '6px',
    width: '74px'
  });
  
  // Generar saludo fijo
  useEffect(() => {
    const generateGreeting = () => {
      // Obtener el nombre del usuario
      let userName = '';
      
      if (user) {
        if (user.firstName) {
          userName = user.firstName;
        }
        else if (user.displayName) {
          userName = user.displayName.split(' ')[0];
        }
        else if (user.email) {
          userName = user.email.split('@')[0];
        }
      }
      
      if (!userName) {
        userName = "there";
      }
      
      return `Hey, ${userName}!`;
    };
    
    setGreeting(generateGreeting());
  }, [user]);
  
  // Actualizar la posición del indicador cuando cambia la pestaña activa
  useEffect(() => {
    const updateIndicator = () => {
      if (tabRefs.current[activeTab]?.current) {
        const tabElement = tabRefs.current[activeTab].current;
        const tabsContainer = tabsContainerRef.current;
        
        if (tabElement && tabsContainer) {
          // Obtener la posición del contenedor de pestañas
          const containerRect = tabsContainer.getBoundingClientRect();
          // Obtener la posición de la pestaña activa
          const tabRect = tabElement.getBoundingClientRect();
          
          // Calcular la posición relativa de la pestaña dentro del contenedor
          const left = tabRect.left - containerRect.left;
          
          setIndicatorStyle({
            left: `${left}px`,
            width: `${tabRect.width}px`
          });
        }
      }
    };
    
    // Pequeño retraso para asegurar que los elementos estén renderizados
    const timer = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timer);
  }, [activeTab, tabRefs]);
  
  // Generar días de la semana para el calendario
  useEffect(() => {
    const generateCalendarWeek = (date) => {
      const day = new Date(date);
      const dayOfWeek = day.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Ajustar para que la semana comience en lunes
      
      const monday = new Date(day);
      monday.setDate(day.getDate() - diff);
      
      const week = [];
      for (let i = 0; i < 7; i++) {
        const currentDay = new Date(monday);
        currentDay.setDate(monday.getDate() + i);
        week.push({
          date: currentDay,
          dayLetter: ['M', 'T', 'W', 'T', 'F', 'S', 'S'][i],
          dayNumber: currentDay.getDate(),
          isToday: currentDay.toDateString() === new Date().toDateString(), // Comparamos con la fecha actual
        });
      }
      
      return week;
    };
    
    setCalendarWeek(generateCalendarWeek(selectedDate));
  }, [selectedDate]);

  // Cargar perfil y estadísticas
  useEffect(() => {
    const loadProfileAndStats = async () => {
      setLoading(true);
      try {
        // Cargar perfil
        const profileResponse = await agtfitService.getProfile();
   
        let caloriesBase = 2000; // valor por defecto
   
        if (profileResponse.success && profileResponse.data) {
          setProfile(profileResponse.data);
   
          // Establecer objetivos de macros
          if (profileResponse.data.macros) {
            // Determinar el valor de base de calorías
            if (profileResponse.data.calculatedCalories) {
   
              if (typeof profileResponse.data.calculatedCalories === 'object') {
                caloriesBase = profileResponse.data.calculatedCalories.base || 2000;
              } else {
                caloriesBase = profileResponse.data.calculatedCalories;
              }
            }
   
            setStats(prev => {
              const newStats = {
                ...prev,
                calories: { 
                  ...prev.calories, 
                  goal: caloriesBase,
                },
                protein: { ...prev.protein, goal: profileResponse.data.macros.protein || 0 },
                carbs: { ...prev.carbs, goal: profileResponse.data.macros.carbs || 0 },
                fats: { ...prev.fats, goal: profileResponse.data.macros.fats || 0 },
              };

              return newStats;
            });
          }
        }
    
        // Cargar estadísticas del día seleccionado
        const formattedDate = selectedDate.toISOString().split('T')[0];

        // Obtener estadísticas diarias
        const dailyStatsResponse = await agtfitService.getDailyStats(formattedDate);
        
        // Obtener comidas y ejercicios para este día
        const [mealsResponse, exercisesResponse] = await Promise.all([
          agtfitService.getMeals(formattedDate),
          agtfitService.getExercises(formattedDate)
        ]);

        // Procesar respuesta de estadísticas diarias
        if (dailyStatsResponse.success && dailyStatsResponse.data) {
          // Obtener calorías quemadas de las estadísticas del día
          const caloriesBurned = dailyStatsResponse.data.caloriesBurned || 0;

          setStats(prev => ({
            ...prev,
            calories: { 
              ...prev.calories, 
              consumed: dailyStatsResponse.data.calories || 0,
              burned: caloriesBurned,
              goal: prev.calories.goal || caloriesBase // MANTENER el objetivo existente
            },
            protein: { 
              ...prev.protein, 
              consumed: dailyStatsResponse.data.protein || 0 
            },
            carbs: { 
              ...prev.carbs, 
              consumed: dailyStatsResponse.data.carbs || 0 
            },
            fats: { 
              ...prev.fats, 
              consumed: dailyStatsResponse.data.fats || 0 
            },
            water: { 
              ...prev.water, 
              consumed: dailyStatsResponse.data.water || 0 
            },
            steps: { 
              ...prev.steps, 
              count: dailyStatsResponse.data.steps || 0 
            }
          }));
        }
  
        // Procesar respuesta de comidas
        if (mealsResponse.success && mealsResponse.data) {
          setTodayMeals(mealsResponse.data);
        }
  
        // Procesar respuesta de ejercicios
        if (exercisesResponse.success && exercisesResponse.data) {
          setTodayExercises(exercisesResponse.data);
        }
      } catch (error) {
        console.error('Error al cargar perfil y estadísticas:', error);
      } finally {
        setLoading(false);
      }
    };
  
    loadProfileAndStats();
  }, [selectedDate]);

  // ✅ FUNCIÓN PARA PARSEAR EL MENSAJE Y EXTRAER DATOS PARA ACTUALIZACIÓN OPTIMISTA
  const parseMessageForOptimisticUpdates = useCallback((message) => {
    const updates = {
      hasUpdates: false,
      action: null,
      stats: null,
      meal: null,
      exercise: null,
      mealId: null,
      exerciseId: null
    };

    try {
      // Detectar creación de comida
      const mealCreateMatch = message.match(/\[MEAL_CREATE\]\s*({[\s\S]*?})\s*\[\/MEAL_CREATE\]/);
      if (mealCreateMatch) {
        const mealData = JSON.parse(mealCreateMatch[1]);
        updates.hasUpdates = true;
        updates.action = 'create';
        updates.meal = mealData;
        updates.stats = {
          calories: mealData.calories || 0,
          protein: mealData.protein || 0,
          carbs: mealData.carbs || 0,
          fats: mealData.fats || 0
        };
        return updates;
      }

      // Detectar eliminación de comida
      const mealDeleteMatch = message.match(/\[MEAL_DELETE\]\s*({[\s\S]*?})\s*\[\/MEAL_DELETE\]/);
      if (mealDeleteMatch) {
        const deleteData = JSON.parse(mealDeleteMatch[1]);
        updates.hasUpdates = true;
        updates.action = 'delete';
        updates.mealId = deleteData.mealId;

        // Encontrar la comida a eliminar para restar sus valores
        const mealToDelete = todayMeals.find(meal => meal.mealId === deleteData.mealId);
        if (mealToDelete) {
          updates.stats = {
            calories: -(mealToDelete.calories || 0),
            protein: -(mealToDelete.protein || 0),
            carbs: -(mealToDelete.carbs || 0),
            fats: -(mealToDelete.fats || 0)
          };
        }
        return updates;
      }

      // Detectar creación de ejercicio
      const exerciseCreateMatch = message.match(/\[EXERCISE_CREATE\]\s*({[\s\S]*?})\s*\[\/EXERCISE_CREATE\]/);
      if (exerciseCreateMatch) {
        const exerciseData = JSON.parse(exerciseCreateMatch[1]);
        updates.hasUpdates = true;
        updates.action = 'create';
        updates.exercise = exerciseData;
        updates.stats = {
          caloriesBurned: exerciseData.caloriesBurned || 0
        };
        return updates;
      }

      // Detectar eliminación de ejercicio
      const exerciseDeleteMatch = message.match(/\[EXERCISE_DELETE\]\s*({[\s\S]*?})\s*\[\/EXERCISE_DELETE\]/);
      if (exerciseDeleteMatch) {
        const deleteData = JSON.parse(exerciseDeleteMatch[1]);
        updates.hasUpdates = true;
        updates.action = 'delete';
        updates.exerciseId = deleteData.exerciseId;

        // Encontrar el ejercicio a eliminar para restar sus valores
        const exerciseToDelete = todayExercises.find(ex => ex.exerciseId === deleteData.exerciseId);
        if (exerciseToDelete) {
          updates.stats = {
            caloriesBurned: -(exerciseToDelete.caloriesBurned || 0)
          };
        }
        return updates;
      }

      // Detectar actualización de agua
      const waterUpdateMatch = message.match(/\[WATER_UPDATE\]\s*({[\s\S]*?})\s*\[\/WATER_UPDATE\]/);
      if (waterUpdateMatch) {
        const waterData = JSON.parse(waterUpdateMatch[1]);
        updates.hasUpdates = true;
        updates.action = 'update';
        updates.stats = {
          water: waterData.amount || 0
        };
        return updates;
      }

      // Detectar eliminación de agua
      const waterDeleteMatch = message.match(/\[WATER_DELETE\]\s*({[\s\S]*?})\s*\[\/WATER_DELETE\]/);
      if (waterDeleteMatch) {
        const waterData = JSON.parse(waterDeleteMatch[1]);
        updates.hasUpdates = true;
        updates.action = 'delete';
        updates.stats = {
          water: -(waterData.amount || 0)
        };
        return updates;
      }

    } catch (error) {
      console.error('❌ Error parseando mensaje para actualizaciones optimistas:', error);
    }

    return updates;
  }, [todayMeals, todayExercises]);

  // ✅ VERSIÓN ULTRA-RÁPIDA con ACTUALIZACIONES OPTIMISTAS
  useEffect(() => {
    const handleCheckmarkDetected = async (event) => {
      console.log('🎯 Checkmark detectado en FitProfile, actualizando inmediatamente...');
      
      try {
        const message = event.detail?.message || '';
        
        // ✅ PASO 1: ACTUALIZACIÓN OPTIMISTA INMEDIATA (sin esperar API)
        const optimisticUpdates = parseMessageForOptimisticUpdates(message);
        
        if (optimisticUpdates.hasUpdates) {
          console.log('⚡ Aplicando actualizaciones optimistas inmediatas:', optimisticUpdates);
          
          // Actualizar stats inmediatamente
          if (optimisticUpdates.stats) {
            setStats(prev => ({
              ...prev,
              calories: {
                ...prev.calories,
                consumed: Math.max(0, prev.calories.consumed + (optimisticUpdates.stats.calories || 0)),
                // goal se mantiene igual
              },
              protein: {
                ...prev.protein,
                consumed: Math.max(0, prev.protein.consumed + (optimisticUpdates.stats.protein || 0))
              },
              carbs: {
                ...prev.carbs,
                consumed: Math.max(0, prev.carbs.consumed + (optimisticUpdates.stats.carbs || 0))
              },
              fats: {
                ...prev.fats,
                consumed: Math.max(0, prev.fats.consumed + (optimisticUpdates.stats.fats || 0))
              },
              water: {
                ...prev.water,
                consumed: Math.max(0, prev.water.consumed + (optimisticUpdates.stats.water || 0))
              },
            }));
          }
          
          // Actualizar meals optimísticamente
          if (optimisticUpdates.meal) {
            if (optimisticUpdates.action === 'create') {
              const newMeal = {
                mealId: `temp-${Date.now()}`, // ID temporal
                ...optimisticUpdates.meal,
                mealDate: selectedDate.toISOString().split('T')[0],
                isTemporary: true // Marcar como temporal
              };
              setTodayMeals(prev => [...prev, newMeal]);
            } else if (optimisticUpdates.action === 'delete' && optimisticUpdates.mealId) {
              setTodayMeals(prev => prev.filter(meal => meal.mealId !== optimisticUpdates.mealId));
            }
          }
          
          // Actualizar exercises optimísticamente
          if (optimisticUpdates.exercise) {
            if (optimisticUpdates.action === 'create') {
              const newExercise = {
                exerciseId: `temp-${Date.now()}`, // ID temporal
                ...optimisticUpdates.exercise,
                exerciseDate: selectedDate.toISOString().split('T')[0],
                isTemporary: true // Marcar como temporal
              };
              setTodayExercises(prev => [...prev, newExercise]);
            } else if (optimisticUpdates.action === 'delete' && optimisticUpdates.exerciseId) {
              setTodayExercises(prev => prev.filter(ex => ex.exerciseId !== optimisticUpdates.exerciseId));
            }
          }
        }
        
        // ✅ PASO 2: SINCRONIZACIÓN EN BACKGROUND (sin bloquear UI)
        setTimeout(async () => {
          try {
            console.log('🔄 Sincronizando con servidor en background...');
            
            const formattedDate = selectedDate.toISOString().split('T')[0];
            
            // Invalidar caché para forzar datos frescos
            agtfitService.invalidateCache(formattedDate);
            
            // Recargar datos reales del servidor
            const [dailyStatsResponse, mealsResponse, exercisesResponse] = await Promise.all([
              agtfitService.getDailyStats(formattedDate, true),
              agtfitService.getMeals(formattedDate, true),
              agtfitService.getExercises(formattedDate, true)
            ]);
            
            // Actualizar con datos reales del servidor
            if (dailyStatsResponse.success && dailyStatsResponse.data) {
              const serverStats = dailyStatsResponse.data;
              
              setStats(prev => ({
                ...prev,
                calories: {
                  ...prev.calories,
                  consumed: serverStats.calories || 0,
                  burned: serverStats.caloriesBurned || 0,
                  // goal se mantiene igual
                },
                protein: { ...prev.protein, consumed: serverStats.protein || 0 },
                carbs: { ...prev.carbs, consumed: serverStats.carbs || 0 },
                fats: { ...prev.fats, consumed: serverStats.fats || 0 },
                water: { ...prev.water, consumed: serverStats.water || 0 },
                steps: { ...prev.steps, count: serverStats.steps || 0 }
              }));
            }
            
            if (mealsResponse.success && mealsResponse.data) {
              // Reemplazar meals temporales con datos reales del servidor
              setTodayMeals(mealsResponse.data);
            }
            
            if (exercisesResponse.success && exercisesResponse.data) {
              // Reemplazar exercises temporales con datos reales del servidor
              setTodayExercises(exercisesResponse.data);
            }
            
            console.log('✅ Sincronización completada');
            
          } catch (error) {
            console.error('❌ Error en sincronización background:', error);
            
            // En caso de error, revertir actualizaciones optimistas
            // y recargar datos del servidor
            const formattedDate = selectedDate.toISOString().split('T')[0];
            try {
              const [dailyStatsResponse, mealsResponse, exercisesResponse] = await Promise.all([
                agtfitService.getDailyStats(formattedDate),
                agtfitService.getMeals(formattedDate),
                agtfitService.getExercises(formattedDate)
              ]);
              
              if (dailyStatsResponse.success) {
                setStats(prev => ({
                  ...prev,
                  calories: { ...prev.calories, consumed: dailyStatsResponse.data.calories || 0 },
                  protein: { ...prev.protein, consumed: dailyStatsResponse.data.protein || 0 },
                  carbs: { ...prev.carbs, consumed: dailyStatsResponse.data.carbs || 0 },
                  fats: { ...prev.fats, consumed: dailyStatsResponse.data.fats || 0 },
                  water: { ...prev.water, consumed: dailyStatsResponse.data.water || 0 }
                }));
              }
              
              if (mealsResponse.success) setTodayMeals(mealsResponse.data);
              if (exercisesResponse.success) setTodayExercises(exercisesResponse.data);
              
            } catch (fallbackError) {
              console.error('❌ Error en fallback:', fallbackError);
            }
          }
        }, 100); // Mínimo delay para que la UI se actualice primero
        
      } catch (error) {
        console.error('❌ Error en handleCheckmarkDetected:', error);
      }
    };

    document.addEventListener('agtfit-checkmark-detected', handleCheckmarkDetected);
    return () => {
      document.removeEventListener('agtfit-checkmark-detected', handleCheckmarkDetected);
    };
  }, [selectedDate, parseMessageForOptimisticUpdates]);

  // Función para manejar la actualización de calorías quemadas
  const handleCaloriesBurnedChange = async (caloriesBurned) => {
    try {
      // Actualizar el estado local inmediatamente para una respuesta UI rápida
      setStats(prev => ({
        ...prev,
        calories: { 
          ...prev.calories, 
          burned: caloriesBurned,
          goal: prev.calories.goal // MANTENER el objetivo existente
        }
      }));
      
      // Obtener la fecha formateada
      const formattedDate = selectedDate.toISOString().split('T')[0];
      
      // Actualizar las calorías quemadas para este día específico
      await agtfitService.updateDayCaloriesBurned(formattedDate, caloriesBurned);
    } catch (error) {
      console.error('Error al actualizar calorías quemadas:', error);
    }
  };

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
 
    // Si estamos en móvil y cambiamos a Health, mostrar solo Health
    if (isMobile && newValue === 4) {
      setShowHealthTab(true);
    }
 
    // Si estamos en móvil y cambiamos desde Health a otra pestaña, ocultar Health
    if (isMobile && showHealthTab && newValue !== 4) {
      setShowHealthTab(false);
    }
  };
  
  // Manejar cambio de fecha en el calendario
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    // Los datos se cargarán automáticamente gracias al useEffect que depende de selectedDate
  };
  
  // Navegar a la semana anterior
  const goToPreviousWeek = () => {
    const prevWeek = new Date(selectedDate);
    prevWeek.setDate(selectedDate.getDate() - 7);
    setSelectedDate(prevWeek);
  };
  
  // Navegar a la semana siguiente
  const goToNextWeek = () => {
    const nextWeek = new Date(selectedDate);
    nextWeek.setDate(selectedDate.getDate() + 7);
    setSelectedDate(nextWeek);
  };

  // Calcular porcentajes para las barras de progreso
  const getPercentage = (consumed, goal) => {
    if (!goal) return 0;
    const percentage = (consumed / goal) * 100;
    return Math.min(percentage, 100); // Limitar a 100%
  };
  
  // Formatear fecha para mostrar
  const formatMonth = (date) => {
    return new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
  };
  
  // Manejar cuando se añade una comida
  const handleMealAdded = (newMeal) => {
    // Actualizar la lista de comidas
    setTodayMeals(prev => [...prev, newMeal]);
    
    // Actualizar estadísticas
    setStats(prev => ({
      ...prev,
      calories: {
        ...prev.calories,
        consumed: prev.calories.consumed + (newMeal.calories || 0),
        goal: prev.calories.goal // MANTENER el objetivo existente
      },
      protein: {
        ...prev.protein,
        consumed: prev.protein.consumed + (newMeal.protein || 0)
      },
      carbs: {
        ...prev.carbs,
        consumed: prev.carbs.consumed + (newMeal.carbs || 0)
      },
      fats: {
        ...prev.fats,
        consumed: prev.fats.consumed + (newMeal.fats || 0)
      }
    }));
  };
  
  // Manejar cuando se actualiza una comida
  const handleMealUpdated = (updatedMeal) => {
    // Si el alimento está desactivado, no contamos sus calorías en las estadísticas
    const isActive = updatedMeal.isActive !== false; // Por defecto es true
    
    // Encontrar la comida original para calcular la diferencia
    const originalMeal = todayMeals.find(meal => meal.mealId === updatedMeal.mealId);
    
    if (originalMeal) {
      // Calcular las diferencias para actualizar las estadísticas
      let caloriesDiff = 0;
      let proteinDiff = 0;
      let carbsDiff = 0;
      let fatsDiff = 0;
      
      if (updatedMeal.isActive !== undefined) {
        // Si es una activación/desactivación
        caloriesDiff = isActive ? updatedMeal.calories : -originalMeal.calories;
        proteinDiff = isActive ? updatedMeal.protein : -originalMeal.protein;
        carbsDiff = isActive ? updatedMeal.carbs : -originalMeal.carbs;
        fatsDiff = isActive ? updatedMeal.fats : -originalMeal.fats;
      } else {
        // Si es una edición de valores
        caloriesDiff = (updatedMeal.calories || 0) - (originalMeal.calories || 0);
        proteinDiff = (updatedMeal.protein || 0) - (originalMeal.protein || 0);
        carbsDiff = (updatedMeal.carbs || 0) - (originalMeal.carbs || 0);
        fatsDiff = (updatedMeal.fats || 0) - (originalMeal.fats || 0);
      }
      
      // Actualizar estadísticas
      setStats(prev => ({
        ...prev,
        calories: { 
          ...prev.calories, 
          consumed: Math.max(0, prev.calories.consumed + caloriesDiff),
          goal: prev.calories.goal // MANTENER el objetivo existente
        },
        protein: { 
          ...prev.protein, 
          consumed: Math.max(0, prev.protein.consumed + proteinDiff)
        },
        carbs: { 
          ...prev.carbs, 
          consumed: Math.max(0, prev.carbs.consumed + carbsDiff)
        },
        fats: { 
          ...prev.fats, 
          consumed: Math.max(0, prev.fats.consumed + fatsDiff)
        }
      }));
    }
    
    // Actualizar la lista de comidas
    setTodayMeals(prev => prev.map(meal => 
      meal.mealId === updatedMeal.mealId ? updatedMeal : meal
    ));
  };
  
  // Manejar cuando se elimina una comida
  const handleMealDeleted = (mealId) => {
    // Encontrar el alimento antes de eliminarlo para ajustar las estadísticas
    const mealToDelete = todayMeals.find(meal => meal.mealId === mealId);
    
    if (mealToDelete) {
      // Actualizar estadísticas
      setStats(prev => ({
        ...prev,
        calories: { 
          ...prev.calories, 
          consumed: Math.max(0, prev.calories.consumed - mealToDelete.calories),
          goal: prev.calories.goal // MANTENER el objetivo existente
        },
        protein: { 
          ...prev.protein, 
          consumed: Math.max(0, prev.protein.consumed - mealToDelete.protein)
        },
        carbs: { 
          ...prev.carbs, 
          consumed: Math.max(0, prev.carbs.consumed - mealToDelete.carbs)
        },
        fats: { 
          ...prev.fats, 
          consumed: Math.max(0, prev.fats.consumed - mealToDelete.fats)
        }
      }));
    }
    
    // Eliminar el alimento de la lista
    setTodayMeals(prev => prev.filter(meal => meal.mealId !== mealId));
  };

  // Función para manejar la actualización de estadísticas
  const handleStatsUpdated = (updatedStats) => {
    setStats(prevStats => {
      // Si updatedStats contiene calories, asegúrate de preservar el goal
      if (updatedStats.calories) {
        return {
          ...prevStats,
          ...updatedStats,
          calories: {
            ...prevStats.calories,
            ...updatedStats.calories,
            goal: prevStats.calories.goal // MANTENER el objetivo existente
          }
        };
      }
      
      // Si no hay cambios en calories, simplemente actualiza lo que se proporciona
      return {
        ...prevStats,
        ...updatedStats
      };
    });
  };
  
  // Removed unused function: handleOpenFoodSearch

  // Manejar cuando se actualiza el objetivo
  const handleGoalUpdated = async (updatedGoal) => {
    try {
      // Eliminar datos en caché del localStorage
      localStorage.removeItem('agtfit_profile');
  
      // Forzar una recarga completa del perfil desde el servidor
      const profileResponse = await agtfitService.getProfile(true); // Añadir parámetro para forzar recarga
  
      if (profileResponse.success && profileResponse.data) {
        // Actualizar el estado del perfil con los datos nuevos
        setProfile(profileResponse.data);
  
        // Determinar el valor de base de calorías
        let caloriesBase = 2000; // valor por defecto
        
        if (profileResponse.data.calculatedCalories) {
          if (typeof profileResponse.data.calculatedCalories === 'object') {
            caloriesBase = profileResponse.data.calculatedCalories.base || 2000;
          } else {
            caloriesBase = profileResponse.data.calculatedCalories;
          }
        }
        
        // Actualizar los objetivos de macros y calorías en el estado
        setStats(prev => ({
          ...prev,
          calories: { 
            ...prev.calories, 
            goal: caloriesBase
          },
          protein: { 
            ...prev.protein, 
            goal: profileResponse.data.macros?.protein || 0 
          },
          carbs: { 
            ...prev.carbs, 
            goal: profileResponse.data.macros?.carbs || 0 
          },
          fats: { 
            ...prev.fats, 
            goal: profileResponse.data.macros?.fats || 0 
          },
        }));
      }
    } catch (error) {
      console.error('Error al actualizar datos después de cambio de objetivo:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <AppleSpinner sizeDesktop={60} sizeMobile={40} />
      </Box>
    );
  }

  // Definición de las pestañas para el nuevo NavigationBar
  const tabs = [
    { id: 0, label: 'Today' },
    { id: 1, label: 'Food' },
    { id: 2, label: 'Move' },
    { id: 3, label: 'Goals' },
    { id: 4, label: 'Health' } // Nueva pestaña
  ];

  // Renderizar contenido según la pestaña activa
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Dashboard
        return (
          <Grid container spacing={3} direction={isMobile ? "column" : "row"}>
            {/* Detailed Nutrition */}
            <Grid item xs={12} md={6} order={isMobile ? 1 : 2}>
              <DetailedNutrition 
                todayMeals={todayMeals} 
                onMealAdded={handleMealAdded}
                onMealUpdated={handleMealUpdated}
                onMealDeleted={handleMealDeleted}
                selectedDate={selectedDate}
              />
            </Grid>
  
            {/* Actividad y agua */}
            <Grid item xs={12} md={6} order={isMobile ? 1 : 2}>
              <TodaysActivity 
                stats={stats} 
                todayExercises={todayExercises} 
                onStartWorkout={onStartWorkout}
                onStatsUpdated={handleStatsUpdated}
                onCaloriesBurnedChange={handleCaloriesBurnedChange}
                selectedDate={selectedDate}
                ref={todaysActivityRef}
              />
            </Grid>
          </Grid>
        );
  
      case 1: // Food
        return <FoodSearch onSelectMeal={handleMealAdded} selectedDate={selectedDate} />;
  
      case 2: // Move
        return <WorkoutStats selectedDate={selectedDate} />;
  
      case 3: // Goals
        return <FitGoals onGoalUpdated={handleGoalUpdated} />;
  
      case 4: // Health
        return <Health selectedDate={selectedDate} />;
      default:
        return null;
    }
  };

  return (
    <ProfileContainer
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ 
        mb: 4, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: isMobile ? 'nowrap' : 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <GradientTitle variant={isMobile ? "h5" : "h4"} sx={{ mb: 0 }}>
            {greeting}
          </GradientTitle>
 
          {/* Badge de verificación para usuarios PRO */}
          {isPro && (
            <BadgeCheck 
              size={isMobile ? 18 : 22} 
              color={FIT_ORANGE} 
              style={{ marginLeft: '4px' }}
            />
          )}
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          ml: isMobile ? 'auto' : 0
        }}>
          <WorkoutModeButton onStartWorkout={onStartWorkout} />
          <IconButton 
            onClick={() => onEditProfile && onEditProfile()}
            sx={{ 
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              p: 1.5,
              color: FIT_ORANGE // Cambio del icono a naranja
            }}
          >
            <Settings size={20} />
          </IconButton>
        </Box>
      </Box>
      
      {/* Texto de objetivo solo visible en pantallas grandes */}
      {!isMobile && (
        <Typography variant="body1" color="text.secondary" sx={{ mt: -3, mb: 3 }}>
          {profile?.goal === 'loss' && 'Fat Loss'}
          {profile?.goal === 'gain' && 'Muscle Gain'}
          {profile?.goal === 'maintenance' && 'Weight Maintenance'}
          {' • '}
          {profile?.calculatedCalories?.base || profile?.calculatedCalories || 2000} kcal daily goal
        </Typography>
      )}
      
      {/* Calendario */}
      <CalendarContainer
        component={motion.div}
        variants={itemVariants}
      >
        {/* Mes y año centrados */}
        <Typography 
          variant="subtitle1" 
          fontWeight={500} 
          sx={{ 
            mb: 2,
            textAlign: 'center'
          }}
        >
          {formatMonth(selectedDate)} {selectedDate.getFullYear()}
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%',
          maxWidth: 600,
          justifyContent: 'space-between',
          px: 2
        }}>
          <IconButton 
            onClick={goToPreviousWeek} 
            size="small"
            sx={{ color: theme.palette.text.secondary }}
          >
            <ChevronLeft size={20} />
          </IconButton>
          
          <Box sx={{ 
            display: 'flex', 
            flex: 1, 
            justifyContent: 'space-around',
            alignItems: 'center'
          }}>
            {calendarWeek.map((day, index) => {
              const isSelected = day.date.toDateString() === selectedDate.toDateString();
              
              return (
                <DayContainer 
                  key={index} 
                  onClick={() => handleDateSelect(day.date)}
                  isSelected={isSelected}
                  sx={{ height: '70px' }} // Altura fija para mejor alineación
                >
                  <DayLetter isSelected={isSelected}>
                    {day.dayLetter}
                  </DayLetter>
                  <DayNumber isSelected={isSelected}>
                    {day.dayNumber}
                  </DayNumber>
                  <DayDot isSelected={isSelected} />
                </DayContainer>
              );
            })}
          </Box>
          
          <IconButton 
            onClick={goToNextWeek} 
            size="small"
            sx={{ color: theme.palette.text.secondary }}
          >
            <ChevronRight size={20} />
          </IconButton>
        </Box>
      </CalendarContainer>
      
      {/* Contenedor de nutrición centrado y con fondo transparente */}
      <NutritionContainer>
        {/* Calorías con texto centrado y más grande */}
        <Box sx={{ mb: 4, position: 'relative' }}>
          {/* Calcular el objetivo de calorías ajustado y el factor de ajuste para macros */}
          {(() => {
            
            // Usar el valor del perfil como respaldo si stats.calories.goal es 0
            const baseGoal = stats.calories.goal || 
              (profile?.calculatedCalories?.base || 
               (typeof profile?.calculatedCalories === 'number' ? profile.calculatedCalories : 1885));
            
            const caloriesBurned = stats.calories.burned || 0;
            const caloriesConsumed = stats.calories.consumed || 0;
            
            // Calcular calorías ajustadas sumando las calorías base y las quemadas
            const adjustedCaloriesGoal = baseGoal + caloriesBurned;
            
            // Factor de ajuste para los macros proporcional al aumento de calorías
            const caloriesAdjustmentFactor = baseGoal > 0 ? 
              adjustedCaloriesGoal / baseGoal : 1;
            
            return (
              <>
                <Box sx={{ 
                  position: 'relative', 
                  height: 14, 
                  borderRadius: 7, 
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  mb: 1
                }}>
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: `${getPercentage(caloriesConsumed, adjustedCaloriesGoal)}%`,
                      borderRadius: 7,
                      background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)'
                    }}
                  />
                </Box>
                
                {/* Texto centrado sobre la barra mostrando calorías ajustadas */}
                <Typography 
                  variant="h6" 
                  fontWeight={600} 
                  sx={{ 
                    textAlign: 'center', 
                    mt: 1.5,
                    fontSize: '1.3rem'
                  }}
                >
                  {roundNumber(caloriesConsumed)} / {roundNumber(adjustedCaloriesGoal)} kcal
                </Typography>
                
                {/* Macros en formato actualizado con ajuste proporcional */}
                <Grid container spacing={4}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                      Protein
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      {roundNumber(stats.protein.consumed)} / {roundNumber(stats.protein.goal * caloriesAdjustmentFactor)} g
                    </Typography>
                    <Box sx={{ position: 'relative', height: 8, borderRadius: 4, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                      <Box 
                        sx={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          height: '100%',
                          width: `${getPercentage(stats.protein.consumed, stats.protein.goal * caloriesAdjustmentFactor)}%`,
                          borderRadius: 4,
                          background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)'
                        }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                      Carbs
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      {roundNumber(stats.carbs.consumed)} / {roundNumber(stats.carbs.goal * caloriesAdjustmentFactor)} g
                    </Typography>
                    <Box sx={{ position: 'relative', height: 8, borderRadius: 4, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                      <Box 
                        sx={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          height: '100%',
                          width: `${getPercentage(stats.carbs.consumed, stats.carbs.goal * caloriesAdjustmentFactor)}%`,
                          borderRadius: 4,
                          background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)'
                        }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={4}>
                    <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                      Fats
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                      {roundNumber(stats.fats.consumed)} / {roundNumber(stats.fats.goal * caloriesAdjustmentFactor)} g
                    </Typography>
                    <Box sx={{ position: 'relative', height: 8, borderRadius: 4, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}>
                      <Box 
                        sx={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          height: '100%',
                          width: `${getPercentage(stats.fats.consumed, stats.fats.goal * caloriesAdjustmentFactor)}%`,
                          borderRadius: 4,
                          background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)'
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </>
            );
          })()}
        </Box>
      </NutritionContainer>
      
      {/* Nuevo NavigationBar con posicionamiento correcto del indicador y soporte para Health */}
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <StyledTabs ref={tabsContainerRef}>
          <TabIndicator style={indicatorStyle} />
 
          {/* En móvil, mostrar comportamiento especial para Health */}
          {isMobile ? (
            <>
              {/* Si Health está activo, mostrar solo Health con chevron izquierdo */}
              {showHealthTab && activeTab === 4 ? (
                <>
                  {/* Chevron izquierdo para volver a las 4 pestañas originales */}
                  <NavigationButton 
                    onClick={() => {
                      setShowHealthTab(false);
                      handleTabChange(0); // Volver a la primera pestaña
                    }}
                    sx={{ color: FIT_ORANGE }}
                  >
                    <ChevronLeft size={20} />
                  </NavigationButton>
 
                  {/* Mostrar solo la pestaña Health */}
                  <StyledTab 
                    key={tabs[4].id}
                    label={tabs[4].label}
                    disableRipple
                    onClick={() => handleTabChange(tabs[4].id)}
                    className={activeTab === tabs[4].id ? 'Mui-selected' : ''}
                    ref={tabRefs[4]}
                  />
                </>
              ) : (
                <>
                  {/* Mostrar las 4 pestañas originales */}
                  {tabs.slice(0, 4).map((tab, index) => (
                    <StyledTab 
                      key={tab.id}
                      label={tab.label}
                      disableRipple
                      onClick={() => handleTabChange(tab.id)}
                      className={activeTab === tab.id ? 'Mui-selected' : ''}
                      ref={tabRefs[index]}
                    />
                  ))}
 
                  {/* Chevron derecho para mostrar Health */}
                  <NavigationButton 
                    onClick={() => {
                      setShowHealthTab(true);
                      handleTabChange(4);
                    }}
                    sx={{ color: FIT_ORANGE }}
                  >
                    <ChevronRight size={20} />
                  </NavigationButton>
                </>
              )}
            </>
          ) : (
            // En escritorio, mostrar todas las pestañas
            tabs.map((tab, index) => (
              <StyledTab 
                key={tab.id}
                label={tab.label}
                disableRipple
                onClick={() => handleTabChange(tab.id)}
                className={activeTab === tab.id ? 'Mui-selected' : ''}
                ref={tabRefs[index]}
              />
            ))
          )}
        </StyledTabs>
      </Box>

      <Box component={motion.div} variants={itemVariants}>
        {renderTabContent()}
      </Box>
      
      {/* Modal para búsqueda de alimentos */}
      <Dialog 
        open={openFoodSearch} 
        onClose={() => setOpenFoodSearch(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3, // Bordes más redondeados
            overflow: 'hidden' // Asegurar que el contenido no desborde
          }
        }}
      >
        <FoodSearch 
          mealType={selectedMealType}
          isModal={true}
          onClose={() => setOpenFoodSearch(false)}
          onSelectMeal={handleMealAdded}
          selectedDate={selectedDate}
        />
      </Dialog>
    </ProfileContainer>
  );
};

export default FitProfile;